/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput } from '@msdyn365-commerce/core';
import { getCartFromCustomer } from '@msdyn365-commerce/global-state';
import { ProductPrice, ProductsDataActions, ProductSearchResult, ProjectionDomain } from '@msdyn365-commerce/retail-proxy';

import { ArrayExtensions } from './utilities';

/**
 * Input class for the GetPriceForProductSearchResult Data Action.
 */
export class PriceForProductSearchResultInput implements IActionInput {
    public products: ProductSearchResult[];

    public constructor(products: ProductSearchResult[]) {
        this.products = products;
    }

    /**
     * Get the cache key.
     * @returns The cache key.
     */
    public getCacheKey = (): string => 'PriceForProductSearchResult';

    /**
     * Get the cache object type.
     * @returns The cache object type.
     */
    public getCacheObjectType = (): string => 'ProductSearchResult';

    /**
     * Define the data cache type.
     * @returns The cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the GetPriceForProductSearchResultDataAction.
 * @param products -- The product search result.
 * @returns The input.
 */
export const createActivePriceForProductSearchResultInput = (products: ProductSearchResult[]): PriceForProductSearchResultInput => {

    return new PriceForProductSearchResultInput(products);
};

/**
 * The Action Method for the GetPriceForProductSearchResult Data Action
 * Gets the active price for product search result via the getActivePrice RetailServer API.
 * @param input -- The data input.
 * @param context -- The action context.
 * @returns The product search result updated with price.
 */
export async function getPriceForProductSearchResultAction(
    input: PriceForProductSearchResultInput,
    context: IActionContext
): Promise<ProductSearchResult[]> {

    let productRecIds: number[];
    if (ArrayExtensions.hasElements(input.products)) {
        productRecIds = input.products.map(product => product.RecordId);
    } else {
        return Promise.resolve([]);
    }

    const projectDomain: ProjectionDomain = { ChannelId: +context.requestContext.apiSettings.channelId,
        CatalogId: +context.requestContext.apiSettings.catalogId };
    const cart = await getCartFromCustomer(context);
    const affiliation = cart?.AffiliationLines;
    const priceZero = 0;

    try {
        const productPrices: ProductPrice[] = await ProductsDataActions.getActivePricesAsync(
            { callerContext: context, queryResultSettings: {} },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            affiliation,
            true);

        const productResults: ProductSearchResult[] = input.products.map(product => {
            const activePrice = productPrices.find(price => price.ProductId === product.RecordId);
            if (activePrice) {
                product.Price = activePrice.CustomerContextualPrice ?? product.Price;
                product.BasePrice = Math.max(activePrice.BasePrice ?? priceZero,
                    activePrice.TradeAgreementPrice ?? priceZero,
                    activePrice.AdjustedPrice ?? priceZero);
            }
            return product;
        });

        return Promise.resolve(productResults);
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- existing error type.
        context.telemetry.exception(error);
        context.telemetry.debug('[getPriceForProductSearchResultAction]Error executing action');
        throw new Error('[getPriceForProductSearchResultAction]Error executing action');
    }
}

/**
 * The getPriceForProductSearchResult data action.
 */
export const getPriceForProductSearchResultDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-price-for-product-search-result',
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- existing pattern.
    action: <IAction<ProductSearchResult[]>>getPriceForProductSearchResultAction,
    input: createActivePriceForProductSearchResultInput
});

export default getPriceForProductSearchResultDataAction;
